import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { getCountryFlag } from '../utils/countryFlags';
import MyPredictionsSummary from '../components/MyPredictionsSummary';
import TeamWidget from '../components/TeamWidget';
import NewsWidget from '../components/NewsWidget';
import PartialLeaderboardWidget from '../components/PartialLeaderboardWidget';
import TrophyWidget from '../components/TrophyWidget';
import WeeklyWinnersWidget from '../components/WeeklyWinnersWidget';
import PredictionTimeline from '../components/PredictionTimeline';
import ScoringExplained from '../components/ScoringExplained';
import RecentPredictionsWidget from '../components/RecentPredictionsWidget';
import { getTeamLogoUrl } from '../utils/logoUrl';
import { formatDate, parseLocalDate } from '../utils/dateUtils';
import { useHasScoredPredictions } from '../hooks/useHasScoredPredictions';
import { setPostAuthRedirect } from '../utils/postAuthRedirect';

interface UserPrediction {
  id: string;
  homeGoals: number;
  awayGoals: number;
  isBanker: boolean;
  points?: number;
}

interface Match {
  id: string;
  homeTeam: {
    id: string;
    name: string;
    shortName?: string;
    code?: string;
    logoUrl?: string;
  };
  awayTeam: {
    id: string;
    name: string;
    shortName?: string;
    code?: string;
    logoUrl?: string;
  };
  matchDate: string;
  predictionsClose: string;
  predictionsOpen: boolean;
  season: {
    id: string;
    name: string;
    gameType: string;
  };
  matchGroup: {
    id: string;
    name: string;
  };
  userPrediction: UserPrediction | null;
  hasPrediction: boolean;
}

interface MatchesBySeason {
  season: {
    id: string;
    name: string;
    gameType: string;
  };
  matches: Match[];
}

interface Season {
  id: string;
  name: string;
  gameType: string;
  countryCode: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  isPublic: boolean;
  description: string;
  rules: {
    pointsForExactScore: number;
    pointsForCorrectResult: number;
    pointsForBanker: number;
    maxBankersPerRound: number;
  };
}

interface UpcomingMatchesProps {
  embedded?: boolean;
}

interface UpcomingMatchesViewProps extends UpcomingMatchesProps {
  matchesBySeason: MatchesBySeason[];
  loading: boolean;
  error: string | null;
}

const formatMatchDate = (dateString: string) => {
  const utcDate = new Date(dateString);
  const now = new Date();
  
  // Get the date components for comparison (ignoring time)
  const matchDate = new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  // Format time using UTC components (12-hour format)
  const hour = utcDate.getUTCHours();
  const minute = utcDate.getUTCMinutes().toString().padStart(2, '0');
  const ampm = hour >= 12 ? 'pm' : 'am';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const timeString = `${displayHour}:${minute}${ampm}`;

  // Function to convert number to ordinal
  const getOrdinal = (num: number) => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) {
      return num + "st";
    }
    if (j === 2 && k !== 12) {
      return num + "nd";
    }
    if (j === 3 && k !== 13) {
      return num + "rd";
    }
    return num + "th";
  };

  if (matchDate.getTime() === today.getTime()) {
    return `Today, ${timeString}`;
  } else if (matchDate.getTime() === tomorrow.getTime()) {
    return `Tomorrow, ${timeString}`;
  } else if (matchDate.getTime() === yesterday.getTime()) {
    return `Yesterday, ${timeString}`;
  } else {
    const diffTime = matchDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays >= 2 && diffDays <= 21) {
      // For next few weeks, show day name and date
      const weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][utcDate.getUTCDay()];
      const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][utcDate.getUTCMonth()];
      const day = getOrdinal(utcDate.getUTCDate());
      return `${weekday} ${day} ${month}, ${timeString}`;
    } else {
      // For dates further out, show date
      const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][utcDate.getUTCMonth()];
      const day = getOrdinal(utcDate.getUTCDate());
      return `${month} ${day}, ${timeString}`;
    }
  }
};

const useUpcomingMatches = () => {
  const [upcomingMatches, setUpcomingMatches] = useState<Match[]>([]);
  const [matchesBySeason, setMatchesBySeason] = useState<MatchesBySeason[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUpcomingMatches = async () => {
      // Check if we have cached data with expiration
      const cachedMatches = sessionStorage.getItem('upcoming_matches_data');
      const cachedMatchesBySeason = sessionStorage.getItem('upcoming_matches_by_season_data');
      const cacheTimestamp = sessionStorage.getItem('upcoming_matches_timestamp');
      
      if (cachedMatches && cachedMatchesBySeason && cacheTimestamp) {
        try {
          const timestamp = parseInt(cacheTimestamp);
          const now = Date.now();
          const oneHour = 15 * 60 * 1000; // 1 hour in milliseconds
          
          // Check if cache is still valid (less than 1 hour old)
          if (now - timestamp < oneHour) {
            const parsedMatches = JSON.parse(cachedMatches);
            const parsedMatchesBySeason = JSON.parse(cachedMatchesBySeason);
            
            setUpcomingMatches(parsedMatches);
            setMatchesBySeason(parsedMatchesBySeason);
            setLoading(false);
            return;
          } else {
            // Cache expired, remove it
            sessionStorage.removeItem('upcoming_matches_data');
            sessionStorage.removeItem('upcoming_matches_by_season_data');
            sessionStorage.removeItem('upcoming_matches_timestamp');
          }
        } catch (e) {
          // If parsing fails, remove invalid cache
          sessionStorage.removeItem('upcoming_matches_data');
          sessionStorage.removeItem('upcoming_matches_by_season_data');
          sessionStorage.removeItem('upcoming_matches_timestamp');
        }
      }
      
      // Only fetch if we don't have valid cached data
      try {
        const response = await apiClient.get('/matches/dashboard/upcoming');
        setUpcomingMatches(response.data.upcomingMatches);
        setMatchesBySeason(response.data.matchesBySeason);
        
        // Cache the data with timestamp
        sessionStorage.setItem('upcoming_matches_data', JSON.stringify(response.data.upcomingMatches));
        sessionStorage.setItem('upcoming_matches_by_season_data', JSON.stringify(response.data.matchesBySeason));
        sessionStorage.setItem('upcoming_matches_timestamp', Date.now().toString());
      } catch (err) {
        console.error('Error fetching upcoming matches:', err);
        setError('Failed to load upcoming matches');
      } finally {
        setLoading(false);
      }
    };

    fetchUpcomingMatches();
  }, []);

  return { upcomingMatches, matchesBySeason, loading, error };
};

const UpcomingMatches: React.FC<UpcomingMatchesProps> = ({ embedded = false }) => {
  const { matchesBySeason, loading, error } = useUpcomingMatches();
  return <UpcomingMatchesView embedded={embedded} matchesBySeason={matchesBySeason} loading={loading} error={error} />;
};

const UpcomingMatchesView: React.FC<UpcomingMatchesViewProps> = ({
  embedded = false,
  matchesBySeason,
  loading,
  error,
}) => {
  const isPredictionsOpen = (predictionsClose: string) => {
    return new Date() < new Date(predictionsClose);
  };

  const getTeamLabel = (team: Match['homeTeam']) =>
    team.shortName || team.code || team.name;

  const containerClass = embedded
    ? 'mt-8'
    : 'max-w-5xl mx-auto mt-8 px-2 md:px-6';

  if (loading) {
    return (
      <div className={containerClass}>
        <div className="animate-pulse">
          <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded mb-6 w-48"></div>
          <div className="relative md:-mx-6 md:px-6 md:overflow-hidden">
            <div className="overflow-x-auto scrollbar-hide -mx-2 px-2 md:mx-0 md:px-0">
              <div className="flex gap-4 pr-12 md:pr-16">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-44 bg-gray-300 dark:bg-gray-600 rounded flex-shrink-0 w-[60vw] md:w-[280px]"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={containerClass}>
        <div className="card p-8">
          <div className="text-center">
            <p className="text-red-500 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      <div className="mb-8">
        <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
          Upcoming Matches
        </h2>
      </div>

      {matchesBySeason.length === 0 ? (
        <div className="card p-8 text-center">
          <h3 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
            No Upcoming Matches
          </h3>
          <p className="opacity-70 mb-6" style={{ color: 'var(--text-primary)' }}>
            There are no upcoming matches scheduled at the moment.
          </p>
          <Link to="/seasons" className="btn-primary">
            Browse Fixtures
          </Link>
        </div>
      ) : (
        <div className="space-y-8">
          {matchesBySeason
            .filter(seasonGroup => seasonGroup.matches.some(match => new Date(match.matchDate) > new Date()))
            .map((seasonGroup) => {
              const futureMatches = seasonGroup.matches.filter(
                match => new Date(match.matchDate) > new Date()
              );

              return (
                <div key={seasonGroup.season.id}>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                      {seasonGroup.season.name}
                    </h3>
                    <Link
                      to={`/seasons/${seasonGroup.season.id}`}
                      className="text-sm font-medium"
                      style={{ color: 'var(--highlight)' }}
                    >
                      View All Matches →
                    </Link>
                  </div>

                  <div className="relative mb-2 md:-mx-6 md:px-6 md:overflow-hidden">
                    <div className="overflow-x-auto scrollbar-hide -mx-2 px-2 md:mx-0 md:px-0">
                      <div className="flex gap-4 pr-12 md:pr-16" style={{ minWidth: 'max-content' }}>
                        {futureMatches.map((match) => {
                          const canPredict = isPredictionsOpen(match.predictionsClose);

                          return (
                            <Link
                              key={match.id}
                              to={`/predictions/${match.id}`}
                              className="card shadow-none p-4 transition-colors bg-gray-50 hover:bg-gray-100 dark:bg-gray-800/50 dark:hover:bg-gray-800 flex-shrink-0 w-[60vw] md:w-[280px] md:min-w-[280px]"
                            >
                              <div className="text-xs mb-3 opacity-70 whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>
                                {formatMatchDate(match.matchDate)}
                              </div>

                              <div className="flex items-center gap-2 mb-2">
                                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                  {match.homeTeam.logoUrl && (
                                    <img
                                      src={getTeamLogoUrl(match.homeTeam.logoUrl)}
                                      alt=""
                                      className="w-6 h-6 object-contain flex-shrink-0"
                                    />
                                  )}
                                  <span className="text-xs font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                                    {getTeamLabel(match.homeTeam)}
                                  </span>
                                </div>

                                <div className="text-sm font-medium flex-shrink-0" style={{ color: 'var(--text-secondary)' }}>
                                  vs
                                </div>

                                <div className="flex items-center gap-1.5 flex-1 min-w-0 justify-end">
                                  <span className="text-xs font-semibold truncate text-right" style={{ color: 'var(--text-primary)' }}>
                                    {getTeamLabel(match.awayTeam)}
                                  </span>
                                  {match.awayTeam.logoUrl && (
                                    <img
                                      src={getTeamLogoUrl(match.awayTeam.logoUrl)}
                                      alt=""
                                      className="w-6 h-6 object-contain flex-shrink-0"
                                    />
                                  )}
                                </div>
                              </div>

                              <div className="mt-2 pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
                                {match.hasPrediction ? (
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="text-xs min-w-0" style={{ color: 'var(--text-primary)' }}>
                                      Your Prediction:{' '}
                                      <span className="font-semibold tabular-nums">
                                        {match.userPrediction?.homeGoals} - {match.userPrediction?.awayGoals}
                                      </span>
                                      {match.userPrediction?.isBanker && (
                                        <span className="ml-1" style={{ color: 'var(--highlight)' }}>★</span>
                                      )}
                                    </div>
                                    {canPredict && (
                                      <span
                                        className="text-xs underline flex-shrink-0"
                                        style={{ color: 'var(--highlight)' }}
                                      >
                                        Edit
                                      </span>
                                    )}
                                  </div>
                                ) : canPredict ? (
                                  <div className="text-xs font-medium text-center" style={{ color: 'var(--highlight)' }}>
                                    Make Prediction →
                                  </div>
                                ) : (
                                  <div className="text-xs text-center" style={{ color: 'var(--text-secondary)' }}>
                                    Predictions Closed
                                  </div>
                                )}
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                    <div
                      className="absolute top-0 bottom-0 pointer-events-none z-10 w-8 md:w-16 bg-gradient-to-l from-[#eff5f6] via-[#eff5f6]/70 to-transparent dark:from-[#0C0E12] dark:via-[#0C0E12]/70 dark:to-transparent -right-2 md:right-0"
                    />
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};



const MyLeagues: React.FC = () => {
  const navigate = useNavigate();
  const [leagues, setLeagues] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState('');

  const mapMembershipsToLeagues = (memberships: any[]) =>
    (memberships || []).map((membership: any) => ({
      ...membership.league,
      id: membership.league?.id || membership.id,
      membershipId: membership.id,
      role: membership.role,
      totalPoints: membership.totalPoints
    }));

  const fetchMyLeagues = async () => {
      // Check if we have cached data with expiration
      const cachedLeagues = sessionStorage.getItem('home_user_leagues_data');
      const cacheTimestamp = sessionStorage.getItem('home_user_leagues_timestamp');
      
      if (cachedLeagues && cacheTimestamp) {
        try {
          const timestamp = parseInt(cacheTimestamp);
          const now = Date.now();
          const oneHour = 15 * 60 * 1000; // 1 hour in milliseconds
          
          // Check if cache is still valid (less than 1 hour old)
          if (now - timestamp < oneHour) {
            const parsedLeagues = JSON.parse(cachedLeagues);
            setLeagues(mapMembershipsToLeagues(parsedLeagues));
            setLoading(false);
            return;
          } else {
            // Cache expired, remove it
            sessionStorage.removeItem('home_user_leagues_data');
            sessionStorage.removeItem('home_user_leagues_timestamp');
          }
        } catch (e) {
          // If parsing fails, remove invalid cache
          sessionStorage.removeItem('home_user_leagues_data');
          sessionStorage.removeItem('home_user_leagues_timestamp');
        }
      }
      
      // Only fetch if we don't have valid cached data
      try {
        const response = await apiClient.get('/leagues/my');
        setLeagues(mapMembershipsToLeagues(response.data));

        sessionStorage.setItem('home_user_leagues_data', JSON.stringify(response.data));
        sessionStorage.setItem('home_user_leagues_timestamp', Date.now().toString());
      } catch (err) {
        console.error('Error fetching my leagues:', err);
        setError('Failed to load leagues');
      } finally {
        setLoading(false);
      }
  };

  useEffect(() => {
    fetchMyLeagues();
  }, []);

  const closeJoinModal = () => {
    setShowJoinModal(false);
    setJoinCode('');
    setJoinError('');
  };

  const handleJoinByCode = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!joinCode.trim()) {
      setJoinError('Please enter an invite code');
      return;
    }

    try {
      setJoining(true);
      setJoinError('');
      const response = await apiClient.post('/leagues/join-by-code', { inviteCode: joinCode.trim() });

      sessionStorage.removeItem('leagues_data');
      sessionStorage.removeItem('user_leagues_data');
      sessionStorage.removeItem('leagues_cache_timestamp');
      sessionStorage.removeItem('home_user_leagues_data');
      sessionStorage.removeItem('home_user_leagues_timestamp');
      sessionStorage.removeItem('home_main_user_leagues_data');
      sessionStorage.removeItem('home_main_user_leagues_timestamp');

      closeJoinModal();

      const leagueId = response.data?.membership?.leagueId;
      if (leagueId) {
        navigate(`/leagues/${leagueId}`);
      } else {
        setLoading(true);
        await fetchMyLeagues();
      }
    } catch (err: any) {
      setJoinError(err.response?.data?.message || 'Failed to join league');
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto mt-8 px-2 md:px-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded mb-6"></div>
          <div className="grid md:grid-cols-3 lg:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-300 dark:bg-gray-600 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return null;
  }

  if (leagues.length === 0) {
    return (
      <div className="max-w-5xl mx-auto mt-8">
        <h2 className="text-xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
          My Leagues
        </h2>
        <div className="card rounded-lg shadow w-full p-6 md:p-8 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex flex-col md:flex-row-reverse md:items-center md:justify-between gap-6">
            <img
              src="/files/images/predictorpro-guys.png"
              alt="Predictor Pro Guys"
              className="w-72 md:w-72 h-auto mx-auto md:mx-0 flex-shrink-0"
            />
            <div className="text-center md:text-left md:flex-1 md:pr-6">
              <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                No friends leagues yet...
              </h3>
              <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
                Create a private friends league and invite mates, or join an existing one with an invite code.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                <Link
                  to="/leagues"
                  state={{ openCreateForm: true }}
                  className="btn-primary inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md"
                >
                  Create a League
                </Link>
                <button
                  type="button"
                  onClick={() => setShowJoinModal(true)}
                  className="btn-secondary inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md"
                >
                  Join with Code
                </button>
              </div>
            </div>
          </div>
        </div>

        {showJoinModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                    Join a Friends League
                  </h3>
                  <button
                    type="button"
                    onClick={closeJoinModal}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    aria-label="Close"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                  Enter the invite code from a friend to join their private league.
                </p>
                {joinError && (
                  <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                    {joinError}
                  </div>
                )}
                <form onSubmit={handleJoinByCode} className="space-y-4">
                  <input
                    type="text"
                    placeholder="Enter invite code"
                    value={joinCode}
                    onChange={(e) => setJoinCode(e.target.value)}
                    className="input-field px-3 py-2 rounded-md text-sm w-full"
                    autoFocus
                  />
                  <div className="flex gap-3 justify-end">
                    <button
                      type="button"
                      onClick={closeJoinModal}
                      className="btn-secondary px-4 py-2 rounded-md text-sm font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={joining}
                      className="btn-primary px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
                    >
                      {joining ? 'Joining...' : 'Join League'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto mt-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
          My Leagues
        </h2>
        <Link
          to="/leagues"
          className="text-sm font-medium"
          style={{ color: 'var(--highlight)' }}
        >
          Create or Join Leagues →
        </Link>
      </div>

      <div className="relative mb-8 md:-mx-6 md:px-6 md:overflow-hidden">
        <div className="overflow-x-auto scrollbar-hide -mx-2 px-2 md:mx-0 md:px-0">
          <div className="flex gap-4 pr-12 md:pr-16" style={{ minWidth: 'max-content' }}>
            {leagues.slice(0, 6).map((league) => {
              // On desktop: use 30% width if 4+ tiles (to show peek of 4th tile), otherwise 320px
              // On mobile: use 60vw (60% of viewport width)
              const hasManyTiles = leagues.length >= 4;
              return (
              <Link
                key={league.id}
                to={`/leagues/${league.id}`}
                className="card shadow-none p-4 transition-colors bg-gray-50 hover:bg-gray-100 dark:bg-gray-800/50 dark:hover:bg-gray-800 flex-shrink-0 w-[60vw] md:w-[280px] md:min-w-[280px]"
              >
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white dark:text-black flex-shrink-0" 
                       style={{ backgroundColor: 'var(--highlight)' }}>
                    {league.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                      {league.name}
                    </h3>
                    <p className="text-xs opacity-70 truncate" style={{ color: 'var(--text-primary)' }}>
                      {league.season?.name || ''}
                    </p>
                  </div>
                </div>

{/*             <p className="text-sm mb-3 line-clamp-2 opacity-70" style={{ color: 'var(--text-primary)' }}>
              {league.description || ''}
            </p> */}

                <div className="flex justify-between items-center text-xs">
                  <div className="flex flex-col space-y-1">
{/*                 <span className="opacity-70" style={{ color: 'var(--text-primary)' }}>
                  {league.currentMembers || 0} members
                </span> */}
                    {league.totalPoints !== undefined && (
                      <span className="font-semibold" style={{ color: '#22c55e' }}>
                        {league.totalPoints} pts
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex justify-between items-center text-sm mt-4">
                  <span className="font-medium" style={{ color: 'var(--highlight)' }}>
                    View league →
                  </span>
                </div>
                
              </Link>
              );
            })}
          </div>
        </div>
        {/* Gradient fade overlay on the right side */}
        <div 
          className="absolute top-0 bottom-0 pointer-events-none z-10 w-8 md:w-16 bg-gradient-to-l from-[#eff5f6] via-[#eff5f6]/70 to-transparent dark:from-[#0C0E12] dark:via-[#0C0E12]/70 dark:to-transparent -right-2 md:right-0"
        ></div>
      </div>
    </div>
  );
};

const ActiveSeasons: React.FC = () => {
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchActiveSeasons = async () => {
      // Check if we have cached data with expiration
      const cachedSeasons = sessionStorage.getItem('home_active_seasons_data');
      const cacheTimestamp = sessionStorage.getItem('home_active_seasons_timestamp');
      
      if (cachedSeasons && cacheTimestamp) {
        try {
          const timestamp = parseInt(cacheTimestamp);
          const now = Date.now();
          const oneHour = 15 * 60 * 1000; // 1 hour in milliseconds
          
          // Check if cache is still valid (less than 1 hour old)
          if (now - timestamp < oneHour) {
            const parsedSeasons = JSON.parse(cachedSeasons);
            setSeasons(parsedSeasons);
            setLoading(false);
            return;
          } else {
            // Cache expired, remove it
            sessionStorage.removeItem('home_active_seasons_data');
            sessionStorage.removeItem('home_active_seasons_timestamp');
          }
        } catch (e) {
          // If parsing fails, remove invalid cache
          sessionStorage.removeItem('home_active_seasons_data');
          sessionStorage.removeItem('home_active_seasons_timestamp');
        }
      }
      
      // Only fetch if we don't have valid cached data
      try {
        const response = await apiClient.get('/seasons?active=true');
        setSeasons(response.data);
        
        // Cache the data with timestamp
        sessionStorage.setItem('home_active_seasons_data', JSON.stringify(response.data));
        sessionStorage.setItem('home_active_seasons_timestamp', Date.now().toString());
      } catch (err) {
        console.error('Error fetching active seasons:', err);
        setError('Failed to load seasons');
      } finally {
        setLoading(false);
      }
    };

    fetchActiveSeasons();
  }, []);





  if (loading) {
    return (
      <div className="max-w-5xl mx-auto mt-12 px-2 md:px-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded mb-6"></div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-300 dark:bg-gray-600 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || seasons.length === 0) {
    return null; // Don't show anything if there's an error or no seasons
  }

  return (
    <div className="max-w-5xl mx-auto mt-12 px-2 md:px-6">
      <div className="mb-6">
        <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
          Active Competitions
        </h2>
{/*         <p className="opacity-70" style={{ color: 'var(--text-primary)' }}>
          Choose a competition to view matches and make predictions
        </p> */}
      </div>

      <div className="relative mb-8 md:-mx-6 md:px-6 md:overflow-hidden">
        <div className="overflow-x-auto scrollbar-hide -mx-2 px-2 md:mx-0 md:px-0">
          <div className="flex gap-4 pr-12 md:pr-16" style={{ minWidth: 'max-content' }}>
            {seasons.map(season => (
              <Link
                key={season.id}
                to={`/seasons/${season.id}`}
                className="card shadow-none p-4 transition-colors bg-gray-50 hover:bg-gray-100 dark:bg-gray-800/50 dark:hover:bg-gray-800 flex-shrink-0 w-[60vw] md:w-[280px] md:min-w-[280px]"
              >
                <div className="flex items-center space-x-3 mb-3">
                  <span className="text-xl flex-shrink-0">{getCountryFlag(season.countryCode)}</span>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                      {season.name}
                    </h3>
{/*                 <p className="text-xs capitalize opacity-70" style={{ color: 'var(--text-primary)' }}>
                  {season.gameType}
                </p> */}
                  </div>
                </div>

                <p className="text-sm mb-3 line-clamp-2 opacity-70" style={{ color: 'var(--text-primary)' }}>
                  {season.description}
                </p>

                <div className="flex justify-between items-center text-sm" style={{ color: 'var(--text-primary)' }}>
                  {/* <span>{formatDate(season.startDate)} - {formatDate(season.endDate)}</span> */}
                  <span className="font-medium" style={{ color: 'var(--highlight)' }}>
                    View matches →
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
        {/* Gradient fade overlay on the right side */}
        <div 
          className="absolute top-0 bottom-0 pointer-events-none z-10 w-8 md:w-16 bg-gradient-to-l from-[#eff5f6] via-[#eff5f6]/70 to-transparent dark:from-[#0C0E12] dark:via-[#0C0E12]/70 dark:to-transparent -right-2 md:right-0"
        ></div>
      </div>

{/*       <div className="text-center">
        <Link 
          to="/seasons"
          className="btn-secondary inline-flex items-center px-4 py-2 text-sm"
        >
          View All Competitions
        </Link>
      </div> */}
    </div>
  );
};

const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Match dates are stored as UK local time in UTC fields, so read the UTC components (same as formatMatchDate)
const formatKickoff = (dateString: string) => {
  const d = new Date(dateString);
  return `${d.getUTCHours().toString().padStart(2, '0')}:${d.getUTCMinutes().toString().padStart(2, '0')}`;
};

const isPremierLeague = (season: MatchesBySeason['season']) =>
  season.id.startsWith('pl-') || /premier league/i.test(season.name);

const getShortTeamLabel = (team: Match['homeTeam']) =>
  team.shortName || team.code || team.name;

const TeamLogo: React.FC<{ team: Match['homeTeam'] }> = ({ team }) =>
  team.logoUrl ? (
    <img src={getTeamLogoUrl(team.logoUrl)} alt="" className="w-6 h-6 object-contain flex-shrink-0" />
  ) : (
    <span className="w-6 h-6 flex-shrink-0" />
  );

const FEATURED_MATCH_LIMIT = 5;

interface FeaturedMatchweekProps {
  group?: MatchesBySeason;
  loading: boolean;
  onPredict: (path: string) => void;
}

const FeaturedMatchweek: React.FC<FeaturedMatchweekProps> = ({ group, loading, onPredict }) => {
  if (loading) {
    return (
      <div className="card shadow bg-gray-50 dark:bg-gray-800/50 p-4 animate-pulse">
        <div className="h-5 bg-gray-300 dark:bg-gray-600 rounded w-48 mb-2"></div>
        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-32 mb-6"></div>
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-8 bg-gray-300 dark:bg-gray-600 rounded mb-3"></div>
        ))}
      </div>
    );
  }

  if (!group) {
    return (
      <div className="card shadow bg-gray-50 dark:bg-gray-800/50 p-8 text-center">
        <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
          No fixtures this week
        </h3>
        <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
          The next round of matches will be up soon.
        </p>
        <Link to="/seasons" className="btn-primary">
          Browse Fixtures
        </Link>
      </div>
    );
  }

  const { season, matches } = group;
  const matchGroupName = matches[0].matchGroup.name;
  const startsWithinWeek = new Date(matches[0].matchDate).getTime() - Date.now() < 7 * 24 * 60 * 60 * 1000;
  const previewMatches = matches.slice(0, FEATURED_MATCH_LIMIT);

  return (
    <div className="card shadow bg-gray-50 dark:bg-gray-800/50 overflow-hidden">
      <div className="px-5 pt-5 pb-4">
        <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: 'var(--highlight)' }}>
          {startsWithinWeek ? 'This week' : 'Next up'}
        </p>
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
          {season.name}
        </h2>
      </div>

      <div className="divide-y border-y" style={{ borderColor: 'var(--border)' }}>
        {previewMatches.map(match => (
          <button
            key={match.id}
            type="button"
            onClick={() => onPredict(`/predictions/${match.id}`)}
            aria-label={`Predict ${match.homeTeam.name} vs ${match.awayTeam.name}`}
            className="w-full grid grid-cols-[1fr_4rem_1fr] items-center gap-2 px-5 py-3 transition-colors hover:bg-black/5 dark:hover:bg-white/5"
            style={{ borderColor: 'var(--border)' }}
          >
            <span className="flex items-center justify-end gap-2.5 min-w-0">
              <span className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                <span className="sm:hidden">{getShortTeamLabel(match.homeTeam)}</span>
                <span className="hidden sm:inline">{match.homeTeam.name}</span>
              </span>
              <TeamLogo team={match.homeTeam} />
            </span>
            <span className="flex flex-col items-center leading-tight">
              <span className="text-[11px] uppercase" style={{ color: 'var(--text-secondary)' }}>
                {WEEKDAYS_SHORT[new Date(match.matchDate).getUTCDay()]}
              </span>
              <span className="text-sm font-medium tabular-nums" style={{ color: 'var(--text-primary)' }}>
                {formatKickoff(match.matchDate)}
              </span>
            </span>
            <span className="flex items-center gap-2.5 min-w-0">
              <TeamLogo team={match.awayTeam} />
              <span className="text-sm font-semibold truncate text-left" style={{ color: 'var(--text-primary)' }}>
                <span className="sm:hidden">{getShortTeamLabel(match.awayTeam)}</span>
                <span className="hidden sm:inline">{match.awayTeam.name}</span>
              </span>
            </span>
          </button>
        ))}
      </div>

      <div className="px-5 py-4 flex flex-col sm:flex-row sm:justify-center">
        <button
          type="button"
          onClick={() => onPredict(`/seasons/${season.id}`)}
          className="btn-primary py-2 px-5"
        >
          Predict {matchGroupName}
        </button>
      </div>
    </div>
  );
};

const FRIENDS_LEAGUE_STEPS = [
  { title: 'Create your league', text: 'Give it a name and pick the competition you want to play.' },
  { title: 'Invite your mates', text: 'Share your invite code in the group chat.' },
  { title: 'Predict and compete', text: 'Climb the table every matchweek, month and season.' },
];

const FriendsLeaguePromo: React.FC<{ onStart: (authPath: '/register' | '/login') => void; onJoin: () => void }> = ({ onStart, onJoin }) => (
  <div className="max-w-5xl mx-auto mt-12 px-2 md:px-6">
    <div className="card shadow bg-gray-50 dark:bg-gray-800/50 p-6 md:p-8">
      <div className="grid md:grid-cols-12 gap-6 md:gap-8 items-center">
        <div className="md:col-span-7">
          <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--highlight)' }}>
            Friends Leagues
          </p>
          <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
            Predict against your friends
          </h2>
          <p className="text-sm md:text-base mb-6" style={{ color: 'var(--text-secondary)' }}>
            Set up a private league and find out who really knows their football.
          </p>

          <ol className="space-y-4 mb-6">
            {FRIENDS_LEAGUE_STEPS.map((step, i) => (
              <li key={step.title} className="flex items-start gap-3">
                <span
                  className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold text-white dark:text-black flex-shrink-0"
                  style={{ backgroundColor: 'var(--highlight)' }}
                >
                  {i + 1}
                </span>
                <div>
                  <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{step.title}</p>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{step.text}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => onStart('/register')}
              className="btn-primary inline-flex items-center justify-center px-5 py-2 text-sm font-medium rounded-md"
            >
              Create a Friends League
            </button>
            <button
              type="button"
              onClick={onJoin}
              className="btn-secondary inline-flex items-center justify-center px-5 py-2 text-sm font-medium rounded-md"
            >
              Got an invite code?
            </button>
          </div>
          <p className="text-sm mt-4" style={{ color: 'var(--text-secondary)' }}>
            Already playing?{' '}
            <button
              type="button"
              onClick={() => onStart('/login')}
              className="font-medium underline"
              style={{ color: 'var(--highlight)' }}
            >
              Sign in to create a league
            </button>
          </p>
        </div>

        <div className="md:col-span-5">
          <img
            src="/files/images/predictorpro-guys.png"
            alt=""
            className="w-64 md:w-full max-w-sm h-auto mx-auto"
          />
        </div>
      </div>
    </div>
  </div>
);

const GuestHome: React.FC = () => {
  const navigate = useNavigate();
  const { matchesBySeason, loading, error } = useUpcomingMatches();

  const now = new Date();
  const groups = matchesBySeason
    .map(group => ({ ...group, matches: group.matches.filter(match => new Date(match.matchDate) > now) }))
    .filter(group => group.matches.length > 0);
  const featured = groups.find(group => isPremierLeague(group.season)) || groups[0];

  // Remember what they were trying to do, then send them through sign up / sign in
  const goViaAuth = (path: string, state?: Record<string, unknown>, authPath: '/register' | '/login' = '/register') => {
    setPostAuthRedirect(path, state);
    navigate(authPath);
  };

  return (
    <>
      <div className="max-w-5xl mx-auto px-2 sm:px-4 md:px-6 md:pt-4">
        <div className="grid gap-8 lg:grid-cols-12 items-start">
          <div className="lg:col-span-6 lg:pt-24 animate-fade-in">
            <p className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: 'var(--highlight)' }}>
              PREDICTORPRO
            </p>
            <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-4" style={{ color: 'var(--text-primary)' }}>
              Predict Like A Pro
            </h1>
            <p className="text-base mb-6" style={{ color: 'var(--text-secondary)' }}>
              Predict Premier League and EFL results, score points each week and battle in global leaderboards or private friends leagues.
            </p>

            <div className="flex gap-3">
              <Link to="/register" className="btn-primary py-2 px-6 text-center flex-1 sm:flex-none">
                Start Playing
              </Link>
              <Link to="/login" className="btn-secondary py-2 px-6 text-center flex-1 sm:flex-none">
                Sign In
              </Link>
            </div>

          </div>

          <div className="lg:col-span-6">
            <FeaturedMatchweek group={featured} loading={loading} onPredict={path => goViaAuth(path)} />
          </div>
        </div>
      </div>

      {loading || groups.length > 0 ? (
        <UpcomingMatchesView
          matchesBySeason={matchesBySeason}
          loading={loading}
          error={error}
        />
      ) : (
        <ActiveSeasons />
      )}

      <FriendsLeaguePromo
        onStart={authPath => goViaAuth('/leagues', { openCreateForm: true }, authPath)}
        onJoin={() => goViaAuth('/leagues', undefined, '/login')}
      />
    </>
  );
};

const Home: React.FC = () => {
  const { user } = useAuth();
  const { hasScoredPredictions, loading: scoredPredictionsLoading } = useHasScoredPredictions();
  const [userLeagues, setUserLeagues] = useState<any[]>([]);
  const [leaguesLoading, setLeaguesLoading] = useState(false);

  // Fetch user's leagues when they're logged in
  useEffect(() => {
    const fetchUserLeagues = async () => {
      if (!user) {
        setUserLeagues([]);
        return;
      }

      // Check if we have cached data with expiration
      const cachedUserLeagues = sessionStorage.getItem('home_main_user_leagues_data');
      const cacheTimestamp = sessionStorage.getItem('home_main_user_leagues_timestamp');
      
      if (cachedUserLeagues && cacheTimestamp) {
        try {
          const timestamp = parseInt(cacheTimestamp);
          const now = Date.now();
          const oneHour = 15 * 60 * 1000; // 1 hour in milliseconds
          
          // Check if cache is still valid (less than 1 hour old)
          if (now - timestamp < oneHour) {
            const parsed = JSON.parse(cachedUserLeagues);
            setUserLeagues(parsed);
            return;
          } else {
            // Cache expired, remove it
            sessionStorage.removeItem('home_main_user_leagues_data');
            sessionStorage.removeItem('home_main_user_leagues_timestamp');
          }
        } catch (e) {
          // If parsing fails, remove invalid cache
          sessionStorage.removeItem('home_main_user_leagues_data');
          sessionStorage.removeItem('home_main_user_leagues_timestamp');
        }
      }

      try {
        setLeaguesLoading(true);
        const response = await apiClient.get('/leagues/my');
        setUserLeagues(response.data || []);
        
        // Cache the data with timestamp
        sessionStorage.setItem('home_main_user_leagues_data', JSON.stringify(response.data || []));
        sessionStorage.setItem('home_main_user_leagues_timestamp', Date.now().toString());
      } catch (error) {
        console.error('Error fetching user leagues:', error);
        setUserLeagues([]);
      } finally {
        setLeaguesLoading(false);
      }
    };

    fetchUserLeagues();
  }, [user]);

  return (
    <>
      {/* Main Content - Different for logged in vs logged out users */}
              {user ? (
          <>
            <div className="max-w-5xl mx-auto mt-8 px-2 sm:px-4 md:px-6">

{/*               <div className="card rounded-lg shadow p-4 md:p-5 mt-1 bg-gray-50 dark:bg-gray-800/50">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
                      Premier League 26/27 and EFL Championship competitions are now live. Check out the latest matches and make your predictions or create your own league to compete with friends.
                    </p>
                  </div>
                  <Link
                    to="/seasons"
                    className="text-sm font-medium whitespace-nowrap flex-shrink-0"
                    style={{ color: 'var(--highlight)' }}
                  >
                    View Competitions →
                  </Link>
                </div>
              </div> */}

              <div className="mb-8 mt-6">
              <TeamWidget />
              </div>

              <div className="mb-8">
                <MyPredictionsSummary compact={true} />
              </div>


              <RecentPredictionsWidget />
              
              <MyLeagues />

              <UpcomingMatches embedded />

              {!scoredPredictionsLoading && hasScoredPredictions && (
                <>
                  <PartialLeaderboardWidget />

                  <PredictionTimeline />

                  {/* <TrophyWidget /> */}

                </>
              )}
              
              {/* <WeeklyWinnersWidget /> */}





              <ScoringExplained showGettingStarted />

            </div>
            <ActiveSeasons />

          </>
        ) : (
        /* Original promotional content for non-logged-in users */
        <>

  

{/*         <div className="max-w-5xl mx-auto mt-8 px-2 sm:px-4 md:px-6">

          <div className={`card p-2 md:p-6 bg-gray-50 dark:bg-gray-800/50`}>  
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2">


            <div className="px-4">
                <div className="mt-4">
                  <strong className="">Getting Started</strong>
                  <p className="text-sm mt-4 mb-4" style={{ color: 'var(--text-secondary)' }}>
                    ✅ &nbsp; <Link to="/register" className="text-sm font-medium" style={{ color: 'var(--highlight)' }}>Pick a league</Link> to join or <Link to="/register" className="text-sm font-medium" style={{ color: 'var(--highlight)' }}>create your own</Link> and invite friends
                  </p>
                  <p className="text-sm mt-4 mb-4" style={{ color: 'var(--text-secondary)' }}>
                    ✅ &nbsp; Make your predictions for the upcoming week
                  </p>
                  <p className="text-sm mt-4 mb-4" style={{ color: 'var(--text-secondary)' }}>
                    ✅ &nbsp; Top your league table and earn the glory of best predictor!
                  </p>
                </div>
              </div>

              <div className="px-4">
                <div className="mt-4">
                  <strong className="">Scoring Explained</strong>
                  <p className="text-sm mt-4 mb-4" style={{ color: 'var(--text-secondary)' }}>
                  🥇 &nbsp; 1 point for predicting the correct result
                  </p>
                  <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                  ⚽️ &nbsp; 3 points for predicting the exact score
                  </p>
                  <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                  💷 &nbsp; Extra points on your banker prediction
                  </p>
                </div>
              </div>

            </div>
          </div>

        </div>
 */}

        <GuestHome />


{/*         <div className="max-w-5xl mx-auto px-2 sm:px-4 md:px-6">
          <div className="grid md:grid-cols-3 gap-8 mb-12 mt-16">
            <div className="card shadow bg-gray-50 dark:bg-gray-800/50">
              <img src="/files/images/homepage_shot_prediction.png" alt="Predict Like A Pro" className="w-full mx-auto mb-4" />
                <div className="p-4 pb-8">
                <h3 className="text text-center font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Predict Like A Pro</h3>
                <p className="opacity-70 text-sm text-center" style={{ color: 'var(--text-primary)' }}>
                  Make your picks each week to predict match scores and results across the top UK football leagues<br />
                </p>
              </div>
            </div>
            
            <div className="card shadow bg-gray-50 dark:bg-gray-800/50">
            <img src="/files/images/homepage_shot_league.png" alt="Predict Like A Pro" className="w-full mx-auto mb-4" />
              <div className="p-4 pb-8">
                <h3 className="text text-center font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Take On The World</h3>
                <p className="opacity-70 text-sm text-center" style={{ color: 'var(--text-primary)' }}>
                  Battle against friends and predictors worldwide to top the leaderboards each month and over the season
                </p>
              </div>
            </div>
            
            <div className="card shadow bg-gray-50 dark:bg-gray-800/50">
            <img src="/files/images/homepage_shot_friends.png" alt="Predict Like A Pro" className="w-full mx-auto mb-4" />
              <div className="p-4 pb-8">
                <h3 className="text text-center font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Friend &amp; Fan Leagues</h3>
                <p className="opacity-70 text-sm text-center" style={{ color: 'var(--text-primary)' }}>
                  Create or join invite-only leagues to play with friends or take on other supporters in public fan leagues
                </p>
              </div>
            </div>
            
          </div>    
        </div>     */}

        </>
      )}

      {/* News Widget - visible to all visitors */}
      <NewsWidget />

      <div className="max-w-5xl mx-auto mt-8 px-2 md:px-6">
        <div className="grid md:grid-cols-4 gap-4">
          <Link
            to="/my-predictions"
            className="card shadow p-4 text-center hover:shadow-lg transition-shadow"
          >
            <h4 className="font-semibold mb-2" style={{ color: 'var(--highlight)' }}>
              My Predictions
            </h4>
            <p className="text-sm opacity-70" style={{ color: 'var(--text-primary)' }}>
              View your predictions and scores
            </p>
          </Link>
          
          <Link
            to="/leagues"
            className="card shadow p-4 text-center hover:shadow-lg transition-shadow"
          >
            <h4 className="font-semibold mb-2" style={{ color: 'var(--highlight)' }}>
              Create or Join League
            </h4>
            <p className="text-sm opacity-70" style={{ color: 'var(--text-primary)' }}>
              Compete with friends and other fans
            </p>
          </Link>
          
          <Link
            to="/seasons"
            className="card shadow p-4 text-center hover:shadow-lg transition-shadow"
          >
            <h4 className="font-semibold mb-2" style={{ color: 'var(--highlight)' }}>
              Browse Fixtures
            </h4>
            <p className="text-sm opacity-70" style={{ color: 'var(--text-primary)' }}>
              View upcoming matches for each competition
            </p>
          </Link>

          <Link
            to="/leaderboards"
            className="card shadow p-4 text-center hover:shadow-lg transition-shadow"
          >
            <h4 className="font-semibold mb-2" style={{ color: 'var(--highlight)' }}>
              Leaderboards
            </h4>
            <p className="text-sm opacity-70" style={{ color: 'var(--text-primary)' }}>
              See the top predictors across all competitions
            </p>
          </Link>
        </div>
      </div>

    </>
  );
};

export default Home; 